import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import '../AiGovernance/AiGovernance.css';
import './isms.css';
import IsmsNav from './IsmsNav';
import { getOrgPermissions } from '../../utils/permissions';
import {
  listRisks, createRisk, updateRisk, deleteRisk, generateRisks, importRisks, approveRisks, listRiskVersions,
  getSoa, listOrgUsers, downloadFile, userLabel, errMsg, scoreOf, levelOf, levelClass,
} from './ismsApi';

const TREATMENTS = ['MITIGATE', 'ACCEPT', 'TRANSFER', 'AVOID'];
const STATUSES = ['OPEN', 'IN_TREATMENT', 'CLOSED', 'ACCEPTED'];
const SCALE = [1, 2, 3, 4, 5];
const EMPTY = {
  title: '', description: '', threat: '', vulnerability: '', likelihood: 3, impact: 3, treatment: '', treatmentPlan: '',
  targetDate: '', residualLikelihood: '', residualImpact: '', status: 'OPEN', ownerUserId: '', reviewDueDate: '', controlIds: [],
};

export default function RiskRegister() {
  const { t } = useTranslation();
  const { canManageFrameworks } = getOrgPermissions();
  const fileRef = useRef(null);
  const [risks, setRisks] = useState([]);
  const [controls, setControls] = useState([]);
  const [users, setUsers] = useState([]);
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);
  const [edit, setEdit] = useState(null); // risk form (with id when editing)
  const [approve, setApprove] = useState(null); // summary text
  const [importErrors, setImportErrors] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r, v] = await Promise.all([listRisks(), listRiskVersions().catch(() => [])]);
      setRisks(r || []);
      setVersions(v || []);
      setError('');
    } catch (e) {
      setError(errMsg(e, t('Isms.loadFailed', 'Could not load the risk register.')));
    } finally {
      setLoading(false);
    }
  }, [t]);
  useEffect(() => { load(); }, [load]);

  // Controls (for linking) and users (for owners) are only needed when the editor opens.
  const openEditor = async (risk) => {
    setEdit(risk ? {
      ...EMPTY, ...risk,
      treatment: risk.treatment || '', targetDate: risk.targetDate || '', reviewDueDate: risk.reviewDueDate || '',
      residualLikelihood: risk.residualLikelihood || '', residualImpact: risk.residualImpact || '',
      ownerUserId: risk.ownerUserId || '', controlIds: risk.controlIds || [], treatmentPlan: risk.treatmentPlan || '',
    } : { ...EMPTY });
    if (controls.length === 0) getSoa().then((s) => setControls(s?.controls || [])).catch(() => {});
    if (users.length === 0) listOrgUsers().then(setUsers).catch(() => {});
  };

  const save = async () => {
    if (!edit.title.trim()) { setError(t('Isms.titleRequired', 'A title is required.')); return; }
    const body = {
      title: edit.title.trim(), description: edit.description, threat: edit.threat, vulnerability: edit.vulnerability,
      likelihood: Number(edit.likelihood), impact: Number(edit.impact),
      treatment: edit.treatment || null, treatmentPlan: edit.treatmentPlan || null, targetDate: edit.targetDate || null,
      residualLikelihood: edit.residualLikelihood ? Number(edit.residualLikelihood) : null,
      residualImpact: edit.residualImpact ? Number(edit.residualImpact) : null,
      status: edit.status, ownerUserId: edit.ownerUserId ? Number(edit.ownerUserId) : null,
      reviewDueDate: edit.reviewDueDate || null, controlIds: edit.controlIds || [],
    };
    setBusy(true);
    try {
      if (edit.id) await updateRisk(edit.id, body); else await createRisk(body);
      setEdit(null);
      setError('');
      await load();
    } catch (e) {
      setError(errMsg(e, t('Isms.saveFailed', 'Could not save.')));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (r) => {
    if (!window.confirm(t('Isms.confirmDeleteRisk', 'Delete this risk?'))) return;
    try { await deleteRisk(r.id); await load(); } catch (e) { setError(errMsg(e, t('Isms.saveFailed', 'Could not save.'))); }
  };

  const generate = async () => {
    setBusy(true);
    try {
      const r = await generateRisks();
      setMsg(t('Isms.generated', '{{n}} risk(s) added from failing controls.', { n: r?.created ?? 0 }));
      await load();
    } catch (e) { setError(errMsg(e, t('Isms.saveFailed', 'Could not save.'))); } finally { setBusy(false); }
  };

  const onFile = async (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = '';
    if (!file) return;
    setBusy(true);
    try {
      const r = await importRisks(file);
      setImportErrors(r?.errors || []);
      setMsg(t('Isms.imported', 'Import finished: {{c}} created, {{u}} updated, {{e}} row error(s).', { c: r?.created ?? 0, u: r?.updated ?? 0, e: (r?.errors || []).length }));
      await load();
    } catch (err) { setError(errMsg(err, t('Isms.importFailed', 'Import failed.'))); } finally { setBusy(false); }
  };

  const doApprove = async () => {
    setBusy(true);
    try {
      await approveRisks(approve || '');
      setApprove(null);
      setMsg(t('Isms.approved', 'Register approved and a version snapshot was saved.'));
      await load();
    } catch (e) { setError(errMsg(e, t('Isms.saveFailed', 'Could not save.'))); } finally { setBusy(false); }
  };

  const dl = (path, name, mime) => downloadFile(path, name, mime).catch((e) => setError(errMsg(e, t('Isms.downloadFailed', 'Download failed.'))));
  const previewScore = edit ? scoreOf(edit.likelihood, edit.impact) : 0;
  const toggleControl = (id) => setEdit((cur) => ({
    ...cur, controlIds: cur.controlIds.includes(id) ? cur.controlIds.filter((x) => x !== id) : [...cur.controlIds, id],
  }));

  const above = risks.filter((r) => ['HIGH', 'CRITICAL'].includes(r.level)).length;
  const latest = versions[0];

  return (
    <div className="aig-page">
      <div className="aig-header">
        <h1 className="aig-title">{t('Isms.riskTitle', 'Risk Register')}</h1>
        <p className="aig-subtitle">{t('Isms.riskSubtitle', 'ISO 27001 Clauses 6.1.2 and 6.1.3: identify, score and treat risks, then have management approve the register.')}</p>
      </div>
      <div className="aig-stat-bar">
        <div className="aig-stat"><div className="aig-stat-label">{t('Isms.statRisks', 'Risks')}</div><div className="aig-stat-val">{risks.length}</div></div>
        <div className="aig-stat"><div className="aig-stat-label">{t('Isms.statHigh', 'High / Critical')}</div><div className={`aig-stat-val ${above ? 'aig-val-bad' : ''}`}>{above}</div></div>
        <div className="aig-stat"><div className="aig-stat-label">{t('Isms.statOpen', 'Open')}</div><div className="aig-stat-val">{risks.filter((r) => r.status === 'OPEN').length}</div></div>
        <div className="aig-stat"><div className="aig-stat-label">{t('Isms.statApprovedV', 'Approved version')}</div><div className="aig-stat-val">{latest ? `v${latest.version}` : '-'}</div></div>
      </div>
      <IsmsNav />

      {error && <div className="isms-error">{error}</div>}
      {msg && <div className="isms-ok">{msg}</div>}
      {importErrors.length > 0 && (
        <div className="isms-error">
          {importErrors.slice(0, 10).map((x, i) => <div key={i}>{t('Isms.row', 'Row')} {x.row}: {x.message}</div>)}
          {importErrors.length > 10 && <div>+{importErrors.length - 10} {t('Isms.more', 'more')}</div>}
        </div>
      )}

      <div className="aig-toolbar">
        <button type="button" className="aig-btn" onClick={() => dl('/isms/risks/export', 'risk-register.csv', 'text/csv')}>{t('Isms.export', 'Export CSV')}</button>
        <button type="button" className="aig-btn" onClick={() => dl('/isms/risks/template', 'risk-register-template.csv', 'text/csv')}>{t('Isms.template', 'Download template')}</button>
        <div className="aig-spacer" />
        {canManageFrameworks && (
          <>
            <input ref={fileRef} type="file" accept=".csv,text/csv" style={{ display: 'none' }} onChange={onFile} />
            <button type="button" className="aig-btn" disabled={busy} onClick={() => fileRef.current && fileRef.current.click()}>{t('Isms.import', 'Import CSV')}</button>
            <button type="button" className="aig-btn" disabled={busy} onClick={generate}>{t('Isms.generate', 'Generate from failing controls')}</button>
            <button type="button" className="aig-btn" disabled={busy || risks.length === 0} onClick={() => setApprove('')}>{t('Isms.approveRegister', 'Approve register')}</button>
            <button type="button" className="aig-btn aig-btn-primary" onClick={() => openEditor(null)}>+ {t('Isms.addRisk', 'Add risk')}</button>
          </>
        )}
      </div>

      {loading ? <div className="aig-spinner" /> : risks.length === 0 ? (
        <div className="aig-empty">{t('Isms.noRisks', 'No risks yet. Add one, import a CSV, or generate suggestions from failing controls.')}</div>
      ) : (
        <table className="aig-table">
          <thead><tr>
            <th>{t('Isms.colRisk', 'Risk')}</th><th>{t('Isms.colScore', 'Score')}</th><th>{t('Isms.colTreatment', 'Treatment')}</th>
            <th>{t('Isms.colResidual', 'Residual')}</th><th>{t('Isms.colStatus', 'Status')}</th><th>{t('Isms.colOwner', 'Owner')}</th><th>{t('Isms.colControls', 'Controls')}</th><th />
          </tr></thead>
          <tbody>
            {risks.map((r) => (
              <tr key={r.id}>
                <td><strong>{r.title}</strong>{r.source && r.source !== 'MANUAL' && <div className="aig-muted">{r.source}</div>}</td>
                <td><span className={`aig-badge ${levelClass(r.level)}`}>{r.level} {r.inherentScore}</span></td>
                <td>{r.treatment || <span className="aig-muted">-</span>}</td>
                <td>{r.residualScore ? <span className={`aig-badge ${levelClass(levelOf(r.residualScore))}`}>{r.residualScore}</span> : <span className="aig-muted">-</span>}</td>
                <td>{r.status}</td>
                <td>{r.ownerEmail || <span className="aig-muted">-</span>}</td>
                <td>{(r.controlIds || []).length}</td>
                <td style={{ whiteSpace: 'nowrap' }}>
                  {canManageFrameworks && (
                    <>
                      <button type="button" className="isms-link" onClick={() => openEditor(r)}>{t('Isms.edit', 'Edit')}</button>{' '}
                      <button type="button" className="isms-link isms-link-danger" onClick={() => remove(r)}>{t('Isms.delete', 'Delete')}</button>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {versions.length > 0 && (
        <div className="isms-panel" style={{ marginTop: '1rem' }}>
          <h4>{t('Isms.versions', 'Approved versions')}</h4>
          <table className="aig-table">
            <tbody>
              {versions.map((v) => (
                <tr key={v.id}><td>v{v.version}</td><td>{v.approvedBy}</td><td>{v.approvedAt ? String(v.approvedAt).slice(0, 10) : ''}</td><td>{v.riskCount} {t('Isms.risksLower', 'risks')}</td><td>{v.summary}</td></tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {edit && (
        <div className="aig-overlay" onClick={() => setEdit(null)}>
          <div className="aig-modal isms-modal-wide" onClick={(e) => e.stopPropagation()}>
            <h3>{edit.id ? t('Isms.editRisk', 'Edit risk') : t('Isms.addRisk', 'Add risk')}</h3>
            <div className="aig-field"><label>{t('Isms.fTitle', 'Title')}</label><input value={edit.title} onChange={(e) => setEdit({ ...edit, title: e.target.value })} /></div>
            <div className="aig-field"><label>{t('Isms.fDesc', 'Description')}</label><textarea rows={2} value={edit.description || ''} onChange={(e) => setEdit({ ...edit, description: e.target.value })} /></div>
            <div className="isms-grid2">
              <div className="aig-field"><label>{t('Isms.fThreat', 'Threat')}</label><input value={edit.threat || ''} onChange={(e) => setEdit({ ...edit, threat: e.target.value })} /></div>
              <div className="aig-field"><label>{t('Isms.fVuln', 'Vulnerability')}</label><input value={edit.vulnerability || ''} onChange={(e) => setEdit({ ...edit, vulnerability: e.target.value })} /></div>
              <div className="aig-field"><label>{t('Isms.fLikelihood', 'Likelihood (1-5)')}</label>
                <select value={edit.likelihood} onChange={(e) => setEdit({ ...edit, likelihood: e.target.value })}>{SCALE.map((n) => <option key={n} value={n}>{n}</option>)}</select></div>
              <div className="aig-field"><label>{t('Isms.fImpact', 'Impact (1-5)')}</label>
                <select value={edit.impact} onChange={(e) => setEdit({ ...edit, impact: e.target.value })}>{SCALE.map((n) => <option key={n} value={n}>{n}</option>)}</select></div>
            </div>
            <div className="isms-score">
              {t('Isms.score', 'Inherent score')}: <strong>{previewScore}</strong>{' '}
              <span className={`aig-badge ${levelClass(levelOf(previewScore))}`}>{levelOf(previewScore)}</span>
            </div>
            <div className="isms-grid2">
              <div className="aig-field"><label>{t('Isms.fTreatment', 'Treatment')}</label>
                <select value={edit.treatment} onChange={(e) => setEdit({ ...edit, treatment: e.target.value })}>
                  <option value="">-</option>{TREATMENTS.map((x) => <option key={x} value={x}>{x}</option>)}</select></div>
              <div className="aig-field"><label>{t('Isms.fStatus', 'Status')}</label>
                <select value={edit.status} onChange={(e) => setEdit({ ...edit, status: e.target.value })}>{STATUSES.map((x) => <option key={x} value={x}>{x}</option>)}</select></div>
              <div className="aig-field"><label>{t('Isms.fResL', 'Residual likelihood')}</label>
                <select value={edit.residualLikelihood} onChange={(e) => setEdit({ ...edit, residualLikelihood: e.target.value })}>
                  <option value="">-</option>{SCALE.map((n) => <option key={n} value={n}>{n}</option>)}</select></div>
              <div className="aig-field"><label>{t('Isms.fResI', 'Residual impact')}</label>
                <select value={edit.residualImpact} onChange={(e) => setEdit({ ...edit, residualImpact: e.target.value })}>
                  <option value="">-</option>{SCALE.map((n) => <option key={n} value={n}>{n}</option>)}</select></div>
              <div className="aig-field"><label>{t('Isms.fTarget', 'Target date')}</label><input type="date" value={edit.targetDate} onChange={(e) => setEdit({ ...edit, targetDate: e.target.value })} /></div>
              <div className="aig-field"><label>{t('Isms.fReview', 'Next review')}</label><input type="date" value={edit.reviewDueDate} onChange={(e) => setEdit({ ...edit, reviewDueDate: e.target.value })} /></div>
            </div>
            <div className="aig-field"><label>{t('Isms.fPlan', 'Treatment plan')}</label><textarea rows={2} value={edit.treatmentPlan} onChange={(e) => setEdit({ ...edit, treatmentPlan: e.target.value })} /></div>
            <div className="aig-field"><label>{t('Isms.fOwner', 'Risk owner')}</label>
              <select value={edit.ownerUserId} onChange={(e) => setEdit({ ...edit, ownerUserId: e.target.value })}>
                <option value="">-</option>{users.map((u) => <option key={u.id} value={u.id}>{userLabel(u)}</option>)}</select></div>
            <div className="aig-field"><label>{t('Isms.fControls', 'Linked Annex A controls')} ({(edit.controlIds || []).length})</label>
              <div className="isms-multi">
                {controls.length === 0 ? <span className="aig-muted">{t('Isms.loadingControls', 'Loading controls...')}</span> : controls.map((c) => (
                  <label key={c.controlId}>
                    <input type="checkbox" checked={(edit.controlIds || []).includes(c.controlId)} onChange={() => toggleControl(c.controlId)} />
                    <span><strong>{c.controlRef}</strong> {c.name}</span>
                  </label>
                ))}
              </div>
            </div>
            <div className="aig-modal-actions">
              <button type="button" className="aig-btn" onClick={() => setEdit(null)}>{t('common.cancel', 'Cancel')}</button>
              <button type="button" className="aig-btn aig-btn-primary" disabled={busy} onClick={save}>{t('common.save', 'Save')}</button>
            </div>
          </div>
        </div>
      )}

      {approve !== null && (
        <div className="aig-overlay" onClick={() => setApprove(null)}>
          <div className="aig-modal" onClick={(e) => e.stopPropagation()}>
            <h3>{t('Isms.approveRegister', 'Approve register')}</h3>
            <p className="aig-muted" style={{ fontSize: '0.8125rem' }}>{t('Isms.approveHelp', 'This saves a dated, read-only snapshot of the whole register as the next version. Record who approved it on behalf of management in the summary.')}</p>
            <div className="aig-field"><label>{t('Isms.summary', 'Summary')}</label><textarea rows={3} value={approve} onChange={(e) => setApprove(e.target.value)} /></div>
            <div className="aig-modal-actions">
              <button type="button" className="aig-btn" onClick={() => setApprove(null)}>{t('common.cancel', 'Cancel')}</button>
              <button type="button" className="aig-btn aig-btn-primary" disabled={busy} onClick={doApprove}>{t('Isms.approve', 'Approve')}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
